/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import { LogLevel } from "@azure/msal-browser";

import { getEnv, isProd } from "./utils/env";

const MSAL_AUTHORITY_ID = getEnv("VITE_MSAL_AUTHORITY_ID");
const MSAL_CLIENT_ID = getEnv("VITE_MSAL_CLIENT_ID");
const MSAL_REDIRECT_URI =
  getEnv("VITE_MSAL_REDIRECT_URI") || window.location.origin + "/auth-callback";
const MSAL_SCOPE = getEnv("VITE_MSAL_SCOPE") || "openid profile email";

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: MSAL_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${MSAL_AUTHORITY_ID}`,
    redirectUri: MSAL_REDIRECT_URI,
    navigateToLoginRequestUrl: false, // Prevent navigation to login request URL
    postLogoutRedirectUri: window.location.origin + "/",
  },
  cache: {
    // MDRS-556: tab-scoped so tokens don't persist or reach other tabs; a new
    // tab signs in via ssoSilent() (utils/msalTabSession.ts).
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: true, // Set to true to help with state management
    secureCookies: false, // Set to true in production with HTTPS
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        // Disable MSAL console logging in production builds. Enable only when explicitly
        // debugging locally via VITE_AUTH_DEBUG=true.
        const authDebugEnabled = !isProd() && getEnv("VITE_AUTH_DEBUG") === "true";
        if (!authDebugEnabled) return;
        switch (level) {
          case LogLevel.Error:
            // eslint-disable-next-line no-console
            console.error(message);
            return;
          case LogLevel.Info:
            // eslint-disable-next-line no-console
            console.info(message);
            return;
          case LogLevel.Verbose:
            // eslint-disable-next-line no-console
            console.debug(message);
            return;
          case LogLevel.Warning:
            // eslint-disable-next-line no-console
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
    allowNativeBroker: false, // Disable native broker to avoid state issues
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [MSAL_SCOPE],
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me", //e.g. https://graph.microsoft.com/v1.0/me
};

export const logoutRequest = {
  // Use full URL to ensure consistent behavior across environments
  postLogoutRedirectUri: window.location.origin + "/",
};
